import { Component, type ErrorInfo, type ReactNode } from 'react'
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded'
import RefreshRounded from '@mui/icons-material/RefreshRounded'
import { getNlsString } from '../../nls/nlsRuntime'
import { Button } from './Button'

// §8.0.1 / §8.1.2 — one component throw used to unmount the entire member
// shell (bottom nav, header, drawers all vanish). Wrap each primary tab body
// in this boundary so a crash stays scoped to the tab and the user can still
// navigate away.

type Props = {
  tabName: string
  resetKey?: unknown
  children: ReactNode
  onReset?: () => void
}

type State = {
  hasError: boolean
  message: string | null
}

export class TabErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: null }

  static getDerivedStateFromError(error: unknown): State {
    const message = error instanceof Error ? error.message : getNlsString('NLSCommonErrorGeneric')
    return { hasError: true, message }
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    // Best-effort console record so downstream telemetry can see the crash
    // without changing existing observability wiring.
    console.error(`[TabErrorBoundary:${this.props.tabName}]`, error, info.componentStack)
  }

  componentDidUpdate(prevProps: Props) {
    // Reset when the parent-supplied identity of the tab or resetKey changes
    // so switching away and back or changing subtabs doesn't stay stuck on a crashed state.
    const tabChanged = prevProps.tabName !== this.props.tabName
    const resetKeyChanged = prevProps.resetKey !== this.props.resetKey
    if ((tabChanged || resetKeyChanged) && this.state.hasError) {
      this.setState({ hasError: false, message: null })
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, message: null })
    this.props.onReset?.()
  }

  render() {
    if (!this.state.hasError) return this.props.children
    return (
      <div
        role="alert"
        className="flex h-full min-h-[240px] flex-1 items-center justify-center p-6"
        data-testid={`tab-error-${this.props.tabName}`}
      >
        <section className="max-w-sm rounded-2xl border border-[var(--border)] bg-[var(--surface-card)] p-5 text-center shadow-[var(--shadow-card)]">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--danger-soft)] text-[var(--danger)]">
            <ErrorOutlineRounded fontSize="medium" />
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)]">{this.props.tabName}</p>
          <h2 className="mt-1 text-lg font-semibold text-[var(--text-strong)]">{getNlsString('NLSCommonTabErrorTitle')}</h2>
          <p className="mt-2 text-sm text-[var(--text)]">
            {getNlsString('NLSCommonTabErrorBody')}
          </p>
          {this.state.message ? (
            <p className="mt-3 truncate text-[11px] text-[var(--muted)]" title={this.state.message}>
              {this.state.message}
            </p>
          ) : null}
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshRounded fontSize="small" />}
            onClick={this.handleRetry}
            className="mt-4"
          >
            {getNlsString('NLSCommonActionReloadThisTab')}
          </Button>
        </section>
      </div>
    )
  }
}
